import { Badge, Button, ButtonLink, Card, Link, Skeleton, Text } from "@backstage/ui";
import { type SwaBundle, type SwaBundleStatus } from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import type { ReactNode } from "react";

import { usePlatformAdmin } from "../identity/ownership";
import { PageHero, PageShell, StateCard } from "../layout/pageShell";

import { ConnectActions } from "./ConnectActions";
import { useMyWebApps } from "./myWebApps";
import { deleteWebAppPath, MY_WEB_APPS_HERO, MY_WEB_APPS_NEW_PATH } from "./swaEntitlement";

const STATUS: Record<SwaBundleStatus, { label: string; color: string }> = {
	provisioning: { label: "Setting up…", color: "var(--bui-fg-warning)" },
	ready: { label: "Ready", color: "var(--bui-fg-success)" },
	deprovisioning: { label: "Deleting…", color: "var(--bui-fg-warning)" },
	absent: { label: "Deleted", color: "var(--bui-fg-secondary)" },
	failed: { label: "Did not finish", color: "var(--bui-fg-danger)" },
};

const useStyles = makeStyles((theme) => ({
	list: {
		display: "grid",
		gridTemplateColumns: "repeat(2, 1fr)",
		gap: theme.spacing(2),
		"@media (max-width: 960px)": { gridTemplateColumns: "1fr" },
	},
	card: {
		display: "grid",
		gap: theme.spacing(1.25),
		alignContent: "start",
		justifyItems: "start",
	},
	head: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
	},
	// Colour sits on a decorative dot: the BUI status colours fail AA contrast as small text.
	dot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block" },
	facts: { display: "grid", gap: theme.spacing(0.5), margin: 0 },
	fact: { overflowWrap: "anywhere" },
	skeleton: { display: "grid", gap: theme.spacing(2.5) },
}));

function Fact({ label, children }: { label: string; children: ReactNode }) {
	const classes = useStyles();
	return (
		<Text as="p" variant="body-small" className={classes.fact}>
			<Text as="span" variant="body-small" color="secondary">
				{label}:
			</Text>{" "}
			{children}
		</Text>
	);
}

/** One bundle: its status and the non-secret facts the registry holds. */
export function WebAppCard({ bundle, admin }: { bundle: SwaBundle; admin: boolean }) {
	const classes = useStyles();
	const { label, color } = STATUS[bundle.status];
	return (
		<Card className={classes.card}>
			<div className={classes.head}>
				<Text as="h2" variant="title-x-small">
					Project {bundle.projectNumber}
				</Text>
				<Badge
					size="small"
					icon={<span className={classes.dot} style={{ background: color }} />}
				>
					{label}
				</Badge>
			</div>
			{bundle.status === "provisioning" && (
				<Text as="p" variant="body-small" color="secondary">
					Setting up takes about 20 minutes. You'll get a notification in Backstage when
					it is ready; you can leave this page.
				</Text>
			)}
			{bundle.status === "ready" && <ConnectActions bundle={bundle} />}
			{bundle.status === "deprovisioning" && (
				<Text as="p" variant="body-small" color="secondary">
					Deleting takes a few minutes. You'll get a notification in Backstage when it is
					done; you can leave this page.
				</Text>
			)}
			{bundle.status === "failed" && (
				<Text as="p" variant="body-small" color="secondary">
					The last setup or delete did not finish. The platform team can see what
					happened; set it up again with the same project number, or reach out if it keeps
					failing.
				</Text>
			)}
			<div className={classes.facts}>
				{bundle.siteUrl && (
					<Fact label="Site">
						<Link href={bundle.siteUrl} target="_blank" rel="noopener noreferrer">
							{bundle.siteUrl}
						</Link>{" "}
						(asks for the visitor password)
					</Fact>
				)}
				{bundle.postgresHost && <Fact label="Database host">{bundle.postgresHost}</Fact>}
				{bundle.keyVaultUri && <Fact label="Key Vault">{bundle.keyVaultUri}</Fact>}
				{/* Plain text: the colleague holds no role at resource-group scope (swa-bundle.md § Who may do what). */}
				{bundle.resourceGroup && <Fact label="Resource group">{bundle.resourceGroup}</Fact>}
				{admin && bundle.runUrl && (
					<Fact label="Last run">
						<Link href={bundle.runUrl} target="_blank" rel="noopener noreferrer">
							{bundle.runUrl}
						</Link>
					</Fact>
				)}
			</div>
			{(bundle.status === "ready" || bundle.status === "failed") && (
				<ButtonLink
					variant="secondary"
					href={deleteWebAppPath(bundle.projectNumber)}
					aria-label={`Delete project ${bundle.projectNumber}`}
				>
					Delete
				</ButtonLink>
			)}
		</Card>
	);
}

/** The shape of the page while the first read is in flight: the hero, then two cards. */
function WebAppsSkeleton() {
	const classes = useStyles();
	return (
		<div className={classes.skeleton} role="status" aria-label="Loading your web apps">
			<div className={classes.skeleton}>
				<Skeleton width={120} height={14} />
				<Skeleton width="min(480px, 90%)" height={44} />
				<Skeleton width={200} height={32} />
			</div>
			<div className={classes.list}>
				{[0, 1].map((n) => (
					<Card key={n}>
						<div className={classes.skeleton}>
							<Skeleton width={160} height={20} />
							<Skeleton width="100%" height={14} />
							<Skeleton width="70%" height={14} />
						</div>
					</Card>
				))}
			</div>
		</div>
	);
}

/** The colleague's own web app bundles (owner-only: the registry serves the session's slug alone). */
export const MyWebAppsPage = () => {
	const classes = useStyles();
	const state = useMyWebApps();
	const { admin } = usePlatformAdmin();

	if (state.loading) {
		return (
			<PageShell>
				<WebAppsSkeleton />
			</PageShell>
		);
	}

	const setUp = (
		<ButtonLink variant="primary" href={MY_WEB_APPS_NEW_PATH}>
			Set up a web app
		</ButtonLink>
	);

	// No list at all when the read failed: an empty one would claim there are no web apps.
	if (state.failed) {
		return (
			<PageShell>
				<PageHero
					eyebrow={MY_WEB_APPS_HERO.eyebrow}
					title="Your web apps could not be loaded."
					meta={setUp}
				/>
				<StateCard
					role="alert"
					title="Couldn't load your web apps"
					action={
						<Button variant="primary" onPress={state.retry}>
							Try again
						</Button>
					}
				>
					The registry did not answer just now. Try again in a moment.
				</StateCard>
			</PageShell>
		);
	}

	// A deleted bundle keeps its row (a new setup reuses it) but is not one of the colleague's web apps.
	const shown = state.bundles.filter((b) => b.status !== "absent");
	const title =
		shown.length === 0
			? "No web apps yet."
			: `${shown.length} web app${shown.length === 1 ? "" : "s"}.`;

	return (
		<PageShell>
			<PageHero eyebrow={MY_WEB_APPS_HERO.eyebrow} title={title} meta={setUp} />
			{shown.length === 0 ? (
				<StateCard title="Nothing set up">
					You have no web apps yet. Set one up for a project to get a password-protected
					website with its own database.
				</StateCard>
			) : (
				<div className={classes.list}>
					{shown.map((bundle) => (
						<WebAppCard key={bundle.bundleId} bundle={bundle} admin={admin} />
					))}
				</div>
			)}
		</PageShell>
	);
};
